<template>
  <div>
    <Feature
      v-if="showMaklerMittelpunktOffer"
      :feature-slug="featureNames.MAKLER_MITTEL"
    >
      <div class="mb-6">
        <ChatWidgetIntegration
          form-key="automaticIntegration"
          :initial-value="widgetInitialValue"
          :busy="widgetIntegrationBusy"
          @confirm="emit('widget-integration', $event)"
        />
      </div>
    </Feature>

    <div class="onboarding-review-acceptances">
      <div class="text-subtitle-2 font-weight-bold mb-3">
        {{ $t('onboarding.wizard.review.acceptances-intro') }}
      </div>

      <div
        v-if="requiresAgbAcceptance"
        class="mb-4"
      >
        <v-checkbox
          :model-value="modelValue.acceptAgb"
          class="onboarding-review-consent-checkbox"
          color="primary"
          density="comfortable"
          hide-details
          @update:model-value="patch({ acceptAgb: !!$event })"
        >
          <template #label>
            <span class="text-body-2 onboarding-review-consent-label">
              {{ $t('onboarding.submit.acceptTerms') }}
              <a
                class="text-primary"
                target="_blank"
                rel="noopener noreferrer"
                :href="publicTOCandLicenceUrl"
              >
                {{ $t('onboarding.submit.termsOf', { company: legalCompanyName }) }}
              </a>
            </span>
          </template>
        </v-checkbox>
      </div>

      <div class="mb-4">
        <v-checkbox
          :model-value="modelValue.acceptPrivacy"
          class="onboarding-review-consent-checkbox"
          color="primary"
          density="comfortable"
          hide-details
          @update:model-value="patch({ acceptPrivacy: !!$event })"
        >
          <template #label>
            <span class="text-body-2 onboarding-review-consent-label">
              {{ $t('onboarding.submit.acceptPrivacy') }}
              <a
                v-if="dataPrivacyUrl"
                class="text-primary"
                target="_blank"
                rel="noopener noreferrer"
                :href="dataPrivacyUrl"
              >
                {{ $t('onboarding.submit.privacyPolicyOf', { company: legalCompanyName }) }}
              </a>
              <span v-else>
                {{ $t('onboarding.submit.privacyPolicyOf', { company: legalCompanyName }) }}
              </span>
              <template v-if="privacyEndSuffix"> {{ '\u00a0' }}{{ privacyEndSuffix }}</template>
            </span>
          </template>
        </v-checkbox>
      </div>

      <Feature
        v-slot="{ feature }"
        :feature-slug="featureNames.MATERIAL_GUIDE"
      >
        <div class="mb-4">
          <v-checkbox
            :model-value="modelValue.acceptGuideline"
            class="onboarding-review-consent-checkbox"
            color="primary"
            density="comfortable"
            hide-details
            @update:model-value="patch({ acceptGuideline: !!$event })"
          >
            <template #label>
              <span class="text-body-2 onboarding-review-consent-label">
                {{ $t('onboarding.submit.acceptGuideline') }}
                <a
                  v-if="guideUrl(feature)"
                  class="text-primary"
                  :href="guideUrl(feature)"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ $t('onboarding.submit.guidelineFor', { platform: readablePlatformName }) }}
                </a>
                <span v-else>
                  {{ $t('onboarding.submit.guidelineFor', { platform: readablePlatformName }) }}
                </span>
                <template v-if="guidelineReceivedSuffix">
                  {{ '\u00a0' }}{{ guidelineReceivedSuffix }}</template
                >
              </span>
            </template>
          </v-checkbox>
        </div>
      </Feature>

      <div class="mb-4">
        <v-checkbox
          :model-value="modelValue.contactViaEmail"
          class="onboarding-review-consent-checkbox"
          color="primary"
          density="comfortable"
          hide-details
          @update:model-value="patch({ contactViaEmail: !!$event })"
        >
          <template #label>
            <span class="text-body-2 onboarding-review-consent-label">
              {{ $t('onboarding.submit.contactViaEmail') }}
            </span>
          </template>
        </v-checkbox>
      </div>

      <div class="mb-4">
        <v-checkbox
          :model-value="modelValue.contactViaWhatsapp"
          class="onboarding-review-consent-checkbox"
          color="primary"
          density="comfortable"
          hide-details
          @update:model-value="patch({ contactViaWhatsapp: !!$event })"
        >
          <template #label>
            <span class="text-body-2 onboarding-review-consent-label">
              {{ $t('onboarding.submit.contactViaWhatsapp') }}
            </span>
          </template>
        </v-checkbox>
      </div>

      <v-alert
        v-if="showSupportAndTipsHint"
        class="mb-4"
        type="info"
        variant="tonal"
        density="comfortable"
      >
        <template #title>
          <span class="text-body-2 font-weight-bold">
            {{ $t('onboarding.submit.supportAndTipsTitle') }}
          </span>
        </template>
        <template #text>
          <span class="text-body-2">
            {{ $t('onboarding.submit.supportAndTipsDescription') }}
          </span>
        </template>
        <template #prepend />
      </v-alert>

      <div class="mb-4">
        <v-checkbox
          :model-value="modelValue.acceptConfirmData"
          class="onboarding-review-consent-checkbox"
          color="primary"
          density="comfortable"
          hide-details
          @update:model-value="patch({ acceptConfirmData: !!$event })"
        >
          <template #label>
            <span class="text-body-2 onboarding-review-consent-label">
              {{ $t('onboarding.submit.confirmAllData') }}
            </span>
          </template>
        </v-checkbox>
      </div>
    </div>

    <div class="d-flex justify-space-between mt-2">
      <v-btn
        variant="outlined"
        rounded="lg"
        @click="emit('back')"
      >
        {{ $t('onboarding.stepContainer.back') }}
      </v-btn>
      <v-btn
        color="primary"
        rounded="lg"
        size="large"
        :disabled="!reviewAcceptancesValid"
        :loading="completeLoading"
        @click="emit('complete')"
      >
        {{ $t('onboarding.wizard.review.complete') }}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ChatWidgetIntegration from '@/components/ai/onboarding/ChatWidgetIntegration.vue'
import type { LegalConsentsValues } from '@/components/ai/onboarding/OnboardingLegalConsents.vue'
import { useBranding } from '@/composables/useBranding'
import featureNames from '@/lib/featureNames'
import { Feature } from '@/plugins/feature-config'

const props = defineProps<{
  modelValue: LegalConsentsValues
  requiresAgbAcceptance: boolean
  showMaklerMittelpunktOffer: boolean
  showSupportAndTipsHint: boolean
  reviewAcceptancesValid: boolean
  widgetInitialValue: boolean | null
  widgetIntegrationBusy: boolean
  completeLoading: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: LegalConsentsValues]
  'widget-integration': [payload: { enabled: boolean }]
  back: []
  complete: []
}>()

const { t, te, locale } = useI18n()
const { publicTOCandLicenceUrl, dataPrivacyUrl, legalCompanyName, readablePlatformName } =
  useBranding()

const guidelineReceivedSuffix = computed(() => {
  if (!te('onboarding.submit.received')) return ''
  const s = t('onboarding.submit.received')
  return typeof s === 'string' && s.trim() ? s.trim() : ''
})
const privacyEndSuffix = computed(() => {
  if (!te('onboarding.submit.acceptPrivacyEnd')) return ''
  const s = t('onboarding.submit.acceptPrivacyEnd')
  return typeof s === 'string' && s.trim() ? s.trim() : ''
})

const guideUrl = (feature: { config?: { guideUrlDE?: string; guideUrlEN?: string } }) => {
  const language = (locale.value || '').toLowerCase().split(/[-_]/)[0]
  return language === 'de' ? feature.config?.guideUrlDE : feature.config?.guideUrlEN
}

const patch = (partial: Partial<LegalConsentsValues>) => {
  emit('update:modelValue', { ...props.modelValue, ...partial })
}
</script>

<style scoped>
.onboarding-review-acceptances :deep(.onboarding-review-consent-checkbox .v-selection-control) {
  align-items: flex-start;
}

.onboarding-review-acceptances :deep(.onboarding-review-consent-checkbox .v-label) {
  opacity: 1;
  height: auto;
  white-space: normal;
}

.onboarding-review-consent-label {
  line-height: 1.5;
  white-space: normal;
}

.onboarding-review-consent-label a {
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
}
</style>
